import { z } from 'zod'

import type { FollowUp } from '@/modules/message/components/follow-ups/types.ts'
import {
  FollowUpRequestBodySchema,
  FollowUpSchema,
} from '@/modules/message/components/follow-ups/types.ts'
import { MessageSchema } from '@/modules/message/service/message-service.tsx'

import { MAX_AUTO_WITHDRAW_TIME_IN_MINUTES } from './consts'

const MessageMediaSchema = z.object({
  sorting: z.number().nullish(),
  mediaId: z.number(),
  preview: z.boolean().nullish(),
})

export type MessageMedia = z.infer<typeof MessageMediaSchema>

export type Message = z.infer<typeof MessageSchema>

const MessageSequenceSchema = z.object({
  id: z.number().nullish().optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']),
  messages: z.array(MessageSchema),
  /** Date Time */
  createdAt: z.string(),
  /** Date Time */
  modifiedAt: z.string(),
  /** Date Time */
  lastSentAt: z.string(),
})

export type MessageSequence = z.infer<typeof MessageSequenceSchema>

const BulkMessageSchema = z.object({
  id: z.number(),
  currentSequence: MessageSequenceSchema,
  legacyDispatchesCount: z.number(),
  totalSent: z.number().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  expiresAt: z.date(),
  startsAt: z.date(),
  fallback: z.string(),
  status: z.enum(['Cancelled', 'Pending', 'Running', 'Done', 'Paused']),
  includeList: z.any(), // Todo
  excludeList: z.any(), // Todo
  sendAsFirstMessage: z.boolean(),
  onlyActiveFans: z.boolean(),
  cooldownDuration: z.number().or(z.string()),
  autoWithdrawHours: z.number().or(z.string().refine(parseInt)),
  autoWithdrawMinutes: z.number().or(z.string().refine(parseInt)),
  autoWithdrawAfterMinutes: z.number().optional(),
  autoWithdrawIfNotReplied: z.boolean(),
  autoWithdrawActivated: z.boolean(),
  createdByBrowserName: z.string(),
  updatedByBrowserName: z.string(),
  minuteDelay: z
    .number()
    .gte(1, 'The number of minutes must be greater than or equal to 1.')
    .int('The number of minutes must be a decimal number.')
    .lte(120, 'The number of minutes must be less than 120.')
    .nullable()
    .optional(),
  followUpEnabled: z.boolean(),
  followUpDto: FollowUpSchema.optional(),
})

export type BulkMessage = z.infer<typeof BulkMessageSchema>

export type MassMessagesFormValues = z.infer<
  typeof MassMessagesCreateFormSchema
>

export const MassMessagesCreateFormSchema = z
  .object({
    messages: z.array(MessageSchema).refine((arr) => arr.length > 0, {
      message: 'Please provide some messages',
    }),
    expiresAt: z.any().refine(
      (date) => {
        const currentDate = new Date()

        return date > currentDate
      },
      {
        message: 'ExpiresAt must be a date in the future',
      },
    ),
    startsAt: z.any(),
    includeList: z.any(),
    excludeList: z.any(),
    cooldownDuration: z.number().or(z.string().refine(parseInt)),
    sendAsFirstMessage: z.boolean(),
    onlyActiveFans: z.boolean(),
    minuteDelay: z
      .number()
      .gte(0, 'The number of minutes must be greater than or equal to 0.')
      .int('The number of minutes must be a decimal number.')
      .lte(120, 'The number of minutes must be less than 120.')
      .nullable(),
    autoWithdrawHours: z
      .number()
      .gte(0, 'Auto withdraw hours must be greater than or equal to 0.')
      .int('Auto withdraw hours must be a decimal number.')
      .lte(23, 'Auto withdraw hours must be less than 24.'),
    autoWithdrawMinutes: z
      .number()
      .gte(0, 'Auto withdraw minutes must be greater than or equal to 0.')
      .int('Auto withdraw minutes must be a decimal number.')
      .lte(59, 'Auto withdraw minutes must be less than 60.'),
    autoWithdrawAfterMinutes: z
      .number()
      .int('Auto withdraw after minutes must be a decimal number.')
      .optional(),
    autoWithdrawIfNotReplied: z.boolean().optional(),
    autoWithdrawActivated: z.boolean(),
    followUpEnabled: z.boolean(),
    followUpDto: FollowUpRequestBodySchema.nullable().optional(),
  })
  .superRefine((schema, refinementContext) => {
    if (schema.autoWithdrawHours || schema.autoWithdrawMinutes) {
      const totalMinutes =
        Number(schema.autoWithdrawHours) * 60 +
        Number(schema.autoWithdrawMinutes)

      if (totalMinutes > MAX_AUTO_WITHDRAW_TIME_IN_MINUTES) {
        return refinementContext.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Auto withdraw time cannot be greater than 24 hours',
          path: ['autoWithdrawHours'],
        })
      }
    }
  })

// Replace with BulkMessageApiV2ResponseDto after Message request update
export interface BulkMessageResponseContent {
  message: BulkMessage
  followUpDto: FollowUp | null
}

export const BulkMessageWithdrawResponse = z.object({
  status: z.enum([
    'FINISHED_WITHDRAWING',
    'WITHDRAWING',
    'WAITING_FOR_BROWSER',
  ]),
  withdrawnMessages: z.number(),
  totalMessages: z.number(),
})

export type MassMessageCreateRequest = Omit<
  MassMessagesFormValues,
  'autoWithdrawHours' | 'autoWithdrawMinutes'
>
